/**
 * What the runs Command can see add up to. Measured only: every number here is
 * counted off rows the browser already holds, nothing is estimated and nothing
 * is expressed as a percentage of a total Command does not know. Reads are
 * capped at the newest fifty runs, so the wording says so rather than implying
 * these are all the runs there have ever been. No I/O and no clock access.
 */
import type { Approval, Plan, RunSummary, RunningTask, Worker } from './data';

export const DAY_MS = 86_400_000;

export interface Snapshot {
  /** Runs that finished since midnight, of the ones Command can see. */
  readonly finishedToday: number;
  readonly doneToday: number;
  readonly failedToday: number;
  /** Of the runs Command can see, how many succeeded and how many did not. */
  readonly done: number;
  readonly failed: number;
  readonly runningNow: number;
  readonly workersWorking: number;
  readonly workersFree: number;
  /**
   * Everything the owner himself has to decide: plans drafted and waiting, and
   * approvals waiting. The header counts plans, so a tile that counted only
   * approvals read 0 beside a badge saying three were waiting.
   */
  readonly waitingOnOwner: number;
}

const WORKING = ['working', 'verifying', 'rotating'];
const FREE = ['idle', 'completed'];

/** Midnight before nowMs, in the reader's own timezone. */
export function startOfDay(nowMs: number): number {
  const date = new Date(nowMs);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

/** The compact line of numbers the operations desk carries. */
export function snapshot(input: {
  results: readonly RunSummary[]; running: readonly RunningTask[];
  workers: readonly Worker[]; approvals: readonly Approval[]; plans: readonly Plan[]; nowMs: number;
}): Snapshot {
  const midnight = startOfDay(input.nowMs);
  let doneToday = 0;
  let failedToday = 0;
  let done = 0;
  let failed = 0;
  for (const result of input.results) {
    const reported = Date.parse(result.reported_at);
    const today = !Number.isNaN(reported) && reported >= midnight;
    if (result.success) {
      done += 1;
      if (today) doneToday += 1;
    } else {
      failed += 1;
      if (today) failedToday += 1;
    }
  }
  return {
    finishedToday: doneToday + failedToday,
    doneToday, failedToday, done, failed,
    runningNow: input.running.length,
    workersWorking: input.workers.filter((w) => WORKING.includes(w.status)).length,
    workersFree: input.workers.filter((w) => FREE.includes(w.status)).length,
    waitingOnOwner: input.approvals.length + input.plans.filter((plan) => plan.status === 'proposed').length,
  };
}

export interface DayOutcome {
  /** Midnight that starts the day, so the bars can be keyed and ordered. */
  readonly dayStartMs: number;
  /** "18 Sep" - short enough for an axis. */
  readonly label: string;
  readonly done: number;
  readonly failed: number;
  readonly total: number;
}

/**
 * Runs per day, oldest first, one entry per day whether or not anything
 * finished - a day with nothing in it is information too.
 */
export function dailyOutcomes(results: readonly RunSummary[], nowMs: number, days: number): DayOutcome[] {
  const today = startOfDay(nowMs);
  const out: DayOutcome[] = [];
  for (let back = days - 1; back >= 0; back -= 1) {
    const dayStartMs = today - back * DAY_MS;
    out.push({
      dayStartMs,
      label: new Date(dayStartMs).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }),
      done: 0, failed: 0, total: 0,
    });
  }
  const first = out[0]!.dayStartMs;
  for (const result of results) {
    const reported = Date.parse(result.reported_at);
    if (Number.isNaN(reported) || reported < first) continue;
    const index = Math.floor((startOfDay(reported) - first) / DAY_MS);
    const day = out[index];
    if (!day) continue;
    const updated = {
      ...day,
      done: day.done + (result.success ? 1 : 0),
      failed: day.failed + (result.success ? 0 : 1),
      total: day.total + 1,
    };
    out[index] = updated;
  }
  return out;
}

/** The tallest bar a chart of these days has to draw, never less than one. */
export function busiestDay(days: readonly DayOutcome[]): number {
  return Math.max(1, ...days.map((d) => d.total));
}
